// Чистые деривации для экранов чата (SPEC §4.2/§4.3). Это «маппинг ленты»: из доменных Message
// (shared-contracts) считаем props для design-system (direction/grouping/deliveryState), вставку
// DateSeparator/UnreadDivider, отображаемое имя/аватар чата (direct-fallback на собеседника). Чистые —
// чтобы инвариант-тест маппинга ловил регресс (SPEC §6). Доменных дублей не вводим (инв.1 хребта).

import type {
  Chat,
  Message,
  PublicUser,
  UserId,
  DeliveryState,
  IsoTimestamp,
} from '@tele-chat/shared-contracts'
import type { TimelineEntry, ReceiptSlice } from '@tele-chat/client-store'
import { authorKeyOf } from './origin.js'

export type BubbleGrouping = 'single' | 'first' | 'middle' | 'last'
export type BubbleDirection = 'in' | 'out'

/** Признак оптимистичного хвоста ленты (client-store §4.3). */
export function isOptimistic(entry: TimelineEntry): boolean {
  return 'optimistic' in entry && entry.optimistic === true
}

/** localState оптимистичного сообщения (queued/sent/failed) или undefined для committed. */
export function optimisticLocalState(entry: TimelineEntry): 'queued' | 'sent' | 'failed' | undefined {
  if ('optimistic' in entry && entry.optimistic === true) {
    return (entry as { localState: 'queued' | 'sent' | 'failed' }).localState
  }
  return undefined
}

/** direction по автору относительно текущего пользователя (SPEC §4.2). */
export function directionOf(entry: TimelineEntry, selfId: UserId): BubbleDirection {
  return entry.senderId === selfId ? 'out' : 'in'
}

/** Порог склейки подряд идущих сообщений одного автора (мс) — «близко по времени» (§0b). */
export const GROUPING_GAP_MS = 5 * 60 * 1000

function closeInTime(a: IsoTimestamp, b: IsoTimestamp): boolean {
  return Math.abs(new Date(a).getTime() - new Date(b).getTime()) <= GROUPING_GAP_MS
}

/**
 * grouping для сообщения по соседям: тот же автор + близко по времени + не на смене дня → склейка.
 * single (один), first (начало серии), middle, last (конец серии). SPEC §4.2.
 */
export function groupingOf(
  entries: readonly TimelineEntry[],
  index: number,
): BubbleGrouping {
  const cur = entries[index]
  if (cur === undefined) return 'single'
  const prev = index > 0 ? entries[index - 1] : undefined
  const next = index < entries.length - 1 ? entries[index + 1] : undefined

  const groupedWithPrev =
    prev !== undefined &&
    authorKeyOf(prev) === authorKeyOf(cur) &&
    prev.kind !== 'system' &&
    cur.kind !== 'system' &&
    sameLocalDay(prev.createdAt, cur.createdAt) &&
    closeInTime(prev.createdAt, cur.createdAt)
  const groupedWithNext =
    next !== undefined &&
    authorKeyOf(next) === authorKeyOf(cur) &&
    next.kind !== 'system' &&
    cur.kind !== 'system' &&
    sameLocalDay(next.createdAt, cur.createdAt) &&
    closeInTime(next.createdAt, cur.createdAt)

  if (!groupedWithPrev && !groupedWithNext) return 'single'
  if (!groupedWithPrev && groupedWithNext) return 'first'
  if (groupedWithPrev && groupedWithNext) return 'middle'
  return 'last'
}

/** Совпадение календарного дня в локальной таймзоне (для разделителей дат). */
export function sameLocalDay(a: IsoTimestamp, b: IsoTimestamp): boolean {
  const da = new Date(a)
  const db = new Date(b)
  return (
    da.getFullYear() === db.getFullYear() &&
    da.getMonth() === db.getMonth() &&
    da.getDate() === db.getDate()
  )
}

/**
 * deliveryState для ИСХОДЯЩЕГО (out) сообщения (SPEC §4.2, FR-DELIVERY-01):
 *   - оптимистичный хвост: queued→'queued', sent→'sent', failed→'failed';
 *   - committed: по receipts — кто-то прочитал → 'read', кто-то доставил → 'delivered', иначе 'sent'.
 * Для входящих (in) статус не показывается (возвращаем undefined).
 */
export function deliveryStateOf(
  entry: TimelineEntry,
  selfId: UserId,
  receiptsFor: (seq: number) => ReceiptSlice,
): DeliveryState | undefined {
  if (entry.senderId !== selfId) return undefined
  const local = optimisticLocalState(entry)
  if (local !== undefined) return local
  const committed = entry as Message
  const r = receiptsFor(committed.seq)
  // «Прочитал/доставил» — кем-то, кроме самого автора (D-09).
  const readBy = r.read.filter((u) => u !== selfId)
  const deliveredBy = r.delivered.filter((u) => u !== selfId)
  if (readBy.length > 0) return 'read'
  if (deliveredBy.length > 0) return 'delivered'
  return 'sent'
}

export type LedgerRow =
  | { type: 'date'; date: IsoTimestamp; key: string }
  | { type: 'unread'; key: string }
  | { type: 'message'; entry: TimelineEntry; grouping: BubbleGrouping; index: number; key: string }

/**
 * Строит «строки ленты» — последовательность разделителей дат, черты непрочитанных и сообщений с
 * посчитанным grouping. Это то, что виртуализатор/рендер ленты обходит сверху вниз (SPEC §4.2).
 * `lastReadSeq` — для постановки UnreadDivider перед первым непрочитанным (только committed с seq>его).
 */
export function buildLedger(
  entries: readonly TimelineEntry[],
  lastReadSeq: number,
): LedgerRow[] {
  const rows: LedgerRow[] = []
  let prevDay: IsoTimestamp | null = null
  let unreadPlaced = false

  for (let i = 0; i < entries.length; i++) {
    const entry = entries[i]
    if (entry === undefined) continue

    // Разделитель даты на смене календарного дня.
    if (prevDay === null || !sameLocalDay(prevDay, entry.createdAt)) {
      rows.push({ type: 'date', date: entry.createdAt, key: `date-${entry.createdAt}` })
      prevDay = entry.createdAt
    }

    // Черта непрочитанных перед первым committed с seq > lastReadSeq.
    if (!unreadPlaced && !isOptimistic(entry)) {
      const seq = (entry as Message).seq
      if (lastReadSeq > 0 && seq > lastReadSeq) {
        rows.push({ type: 'unread', key: 'unread-divider' })
        unreadPlaced = true
      }
    }

    rows.push({
      type: 'message',
      entry,
      grouping: groupingOf(entries, i),
      index: i,
      key: messageKey(entry),
    })
  }
  return rows
}

/** Стабильный ключ строки сообщения: committed по id, оптимистичный по clientMsgId. */
export function messageKey(entry: TimelineEntry): string {
  if ('optimistic' in entry && entry.optimistic === true) {
    return `opt-${(entry as { clientMsgId: string }).clientMsgId}`
  }
  return `msg-${(entry as Message).id}`
}

// ─────────────────────────────────────────────────────────────────────────────
// Имя/аватар чата (direct-fallback на собеседника). SPEC §4.3/§4.4.
// ─────────────────────────────────────────────────────────────────────────────

export interface ChatDisplay {
  title: string
  avatarUrl?: string
  /** Для direct — id собеседника (для presence/профиля). */
  peerId?: UserId
}

/**
 * Отображаемое имя/аватар чата. Для group — из самого Chat; для direct — у собеседника
 * (Chat.title/avatarUrl пустые, §0b): берём PublicUser из резолвера по «другому» memberId.
 */
export function chatDisplay(
  chat: Chat,
  selfId: UserId,
  resolveUser: (id: UserId) => PublicUser | undefined,
): ChatDisplay {
  if (chat.type === 'group') {
    return { title: chat.title ?? 'Группа', avatarUrl: chat.avatarUrl }
  }
  const peerId = chat.memberIds.find((id) => id !== selfId) ?? chat.memberIds[0]
  const peer = peerId !== undefined ? resolveUser(peerId) : undefined
  return {
    title: peer?.name ?? chat.title ?? 'Диалог',
    avatarUrl: peer?.avatarUrl ?? chat.avatarUrl,
    peerId,
  }
}
